import React, { useEffect, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { CalendarCheck, CalendarAlt } from 'theme/icon';
import { connect } from 'react-redux';
import { Translate, I18n } from 'react-redux-i18n';
import * as actions from 'redux/actions/calendarActions';
import Menu from 'menu';
import 'assets/css/calendar.css';

function todayYMD() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function normalizeDateAt(value) {
  if (!value) {
    return '';
  }
  const raw = String(value);
  return raw.includes('T') ? raw.split('T')[0] : raw.slice(0, 10);
}

function Calendar(props) {
  const [isOpen, setIsOpen] = React.useState(false);
  const rootRef = useRef(null);
  const history = useHistory();
  const todayStr = todayYMD();

  const list = Array.isArray(props.list) ? props.list : [];

  const upcomingList = useMemo(() => {
    return list
      .filter((item) => normalizeDateAt(item.date_at) >= todayStr)
      .slice()
      .sort((a, b) => {
        const da = normalizeDateAt(a.date_at);
        const db = normalizeDateAt(b.date_at);
        if (da < db) return -1;
        if (da > db) return 1;
        return Number(a.id) - Number(b.id);
      });
  }, [list, todayStr]);

  const isHolidayToday = useMemo(
    () => upcomingList.some((item) => normalizeDateAt(item.date_at) === todayStr),
    [upcomingList, todayStr]
  );

  const toggleOpen = () => {
    setIsOpen((open) => {
      const next = !open;
      if (next) {
        props.loadList({ lang: props.lang, from: todayStr, size: 100, index: 0 });
      }
      return next;
    });
  };

  const close = () => setIsOpen(false);

  const openFullCalendar = () => {
    close();
    history.push(Menu.calendar.url);
  };

  useEffect(() => {
    if (!props.sessionReady || !props.user_id) {
      return;
    }
    props.loadList({ lang: props.lang, from: todayStr, size: 100, index: 0 });
  }, [props.sessionReady, props.user_id, props.lang]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const onDocClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        close();
      }
    };

    const onEscape = (event) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, [isOpen]);

  const eventName = (item) => {
    const key = `name_${props.lang}`;
    return item[key] || item.name_es || item.name_en || '';
  };

  const isToday = (dateAt) => normalizeDateAt(dateAt) === todayStr;

  return (
    <div className="calendar-root" ref={rootRef}>
      <button
        type="button"
        className={`floating-action-btn calendar-fab${isHolidayToday ? ' calendar-fab--holiday' : ''}`}
        aria-label={I18n.t('calendar_title')}
        aria-expanded={isOpen}
        onClick={toggleOpen}
      >
        {isHolidayToday ? <CalendarCheck /> : <CalendarAlt />}
      </button>

      {isOpen && (
        <>
          <div className="calendar-backdrop" aria-hidden="true" onClick={close} />
          <div
            className="calendar-panel"
            role="dialog"
            aria-label={I18n.t('calendar_title')}
          >
            <div className="calendar-panel-header">
              <button
                type="button"
                className="calendar-panel-title-btn"
                onClick={openFullCalendar}
              >
                <Translate value="calendar_title" />
              </button>
              <button
                type="button"
                className="calendar-panel-close"
                aria-label="Close"
                onClick={close}
              >
                ×
              </button>
            </div>
            <div className="calendar-panel-body">
              {props.loading ? (
                <p className="calendar-empty">
                  <Translate value="report_loading" />
                </p>
              ) : upcomingList.length === 0 ? (
                <p className="calendar-empty">
                  <Translate value="calendar_empty" />
                </p>
              ) : (
                <ul className="calendar-list">
                  {upcomingList.map((item) => (
                    <li
                      key={item.id}
                      className={`calendar-item${isToday(item.date_at) ? ' calendar-item--today' : ''}`}
                    >
                      <span className="calendar-item-icon" aria-hidden="true">
                        {isToday(item.date_at) ? <CalendarCheck /> : <CalendarAlt />}
                      </span>
                      <div className="calendar-item-body">
                        <span className="calendar-item-name">{eventName(item)}</span>
                        <span className="calendar-item-date">{item.date_at}</span>
                        <span className="calendar-item-day">{item.day}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function mapStateToProps(reducer) {
  return {
    user_id: reducer.session.auth.id,
    sessionReady: reducer.session.sessionReady,
    list: reducer.calendar.list,
    error: reducer.calendar.error,
    loading: reducer.calendar.loading,
    lang: reducer.i18n.locale,
  };
}

const mapDispatchToProps = (dispatch) => ({
  loadList: (params) => dispatch(actions.loadList({ params })),
});

export default connect(mapStateToProps, mapDispatchToProps)(Calendar);
